import { Add } from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsReportParams,
  WorkExperienceOptionalSettings,
  WorkExperienceReportCustomParams,
} from '~/data/reports';
import { useWorkExperienceReportCustomParamsSelector } from '~/scenes/PortalModules/Analytics/People/WorkExperienceReport/WorkExperienceReportCustomParamsSelector/useWorkExperienceReportCustomParamsSelector';

type WorkExperienceReportCustomParamsSelectorProps = {
  params: AnalyticsReportParams<WorkExperienceReportCustomParams>;
  onChange: (
    params: AnalyticsReportParams<WorkExperienceReportCustomParams>,
  ) => void;
};

export const WorkExperienceReportCustomParamsSelector = (
  props: WorkExperienceReportCustomParamsSelectorProps,
) => {
  const { params, onChange } = props;

  const { t } = useTranslation();

  const { customParamSwitches, selectOptions, getSelectedOption } =
    useWorkExperienceReportCustomParamsSelector();

  const select = useMemo(() => {
    const selectId = 'optionalDataSelector';

    return {
      id: selectId,
      value: getSelectedOption(params).value,
      label: t('laCompExpCalc'),
      labelId: `${selectId}-label`,
      inputId: `${selectId}-input`,
    };
  }, [t, params, getSelectedOption]);

  const onChangeSelection = useCallback(
    (value: string) => {
      const newParams = Object.fromEntries(
        [
          WorkExperienceOptionalSettings.IncludeCompanyHistory,
          WorkExperienceOptionalSettings.UseOriginalStartDate,
        ].map((key) => [key, key.toString() === value]),
      );

      onChange({ ...params, ...newParams });
    },
    [onChange, params],
  );

  return (
    <Box>
      <FormControl size={'small'} fullWidth={true}>
        <InputLabel id={select.labelId} size={'small'}>
          {select.label}
        </InputLabel>
        <Select
          labelId={select.labelId}
          id={select.id}
          value={select.value}
          onChange={(event) => {
            onChangeSelection(event.target.value);
          }}
          input={<OutlinedInput id={select.inputId} label={select.label} />}
          size={'small'}
          fullWidth={true}
        >
          {selectOptions.map(({ value, label }, index) => (
            <MenuItem
              key={`option-${index}`}
              value={value}
              selected={selectOptions.some((option) => option.value === value)}
            >
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {customParamSwitches.map(({ key, name }, index) => {
        const value = params[key];

        return (
          <Box key={`custom-param-${index}`}>
            <FormControlLabel
              control={<Switch />}
              label={name}
              checked={value}
              onChange={(_event, checked) => {
                onChange({ ...params, [key]: checked });
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

type WorkExperienceReportCustomParamsSelectionProps = {
  enabledCustomParams: WorkExperienceOptionalSettings[];
};

export const WorkExperienceReportCustomParamsSelection = (
  props: WorkExperienceReportCustomParamsSelectionProps,
) => {
  const { enabledCustomParams } = props;

  const { customParamSwitches, getSelectedOption } =
    useWorkExperienceReportCustomParamsSelector();

  const { params, enabledSwitches } = useMemo(() => {
    const params: WorkExperienceReportCustomParams = Object.fromEntries(
      enabledCustomParams.map((param) => [param, true]),
    );

    const enabledSwitches = enabledCustomParams.filter((key) =>
      customParamSwitches.some((s) => s.key === key),
    );

    return {
      params,
      enabledSwitches,
    };
  }, [enabledCustomParams, customParamSwitches]);

  return (
    <Box>
      {getSelectedOption(params).label}
      {enabledSwitches.length > 0 ? (
        <>
          <Add fontSize={'inherit'} color={'inherit'} />
          {customParamSwitches
            .filter((cp) => enabledCustomParams.includes(cp.key))
            .map((cp) => cp.name)
            .join(', ')}
        </>
      ) : null}
    </Box>
  );
};
